/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react'
import { Button, Form, message, Result, Spin, Typography } from 'antd'
import { useSearchParams, unstable_usePrompt } from 'react-router'
import IncidentDetailsFormSection from './sections/details'
import IncidentPeopleFormSection from './sections/people'
import IncidentVehiclesFormSection from './sections/vehicles'
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks'
import StatusE from '../../../../../types/statusE'
import { DEFAULT_SUCCESS_MESSAGE } from '../../../../../constants'
import getStyles from './styles'
import { mapFormValuesToIncidentType } from './utils'
import { IncidentT } from '../../../../../types/incidentT'
import { setStatus } from '../../../slice'
import { createDateFromString } from '../../../../../utils'
import { createIncident } from '../../../actions'
import { useFormPersistence } from '../../../../../hooks'

export default function CreateIncidentForm() {
  const styles = getStyles()
  const [form] = Form.useForm<IncidentT>()
  const dispatch = useAppDispatch()
  const { status, response } = useAppSelector((state) => state.incident)
  const [formTouched, setFormTouched] = useState(false)

  const [searchParams] = useSearchParams()

  const { clearFormData, ensureTokenBeforeSubmit } = useFormPersistence(form, {
    formKey: 'incident-create',
    isFormTouched: formTouched,
    onRestore: () => {
      message.info('Your previously entered data has been restored.')
      setFormTouched(true)
    },
  })

  unstable_usePrompt({
    when: formTouched,
    message: 'You have unsaved changes. Do you really want to leave?',
  })

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (formTouched) {
        event.preventDefault()
        event.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [formTouched])

  useEffect(() => {
    const people = Array.from(searchParams.values())
      .filter((value) => value.match(/{*}/))
      .map((value) => {
        const person = JSON.parse(value)
        return {
          ...person,
          birth_date: person.birth_date ? createDateFromString(person.birth_date) : '',
          type: 'offender',
        }
      })
    form.setFieldValue('people', people)
  }, [searchParams, form])

  useEffect(() => {
    if (status === StatusE.CREATED) {
      clearFormData()
      setFormTouched(false)
    }
  }, [status, clearFormData])

  const onFinish = async (values: IncidentT) => {
    const tokenValid = await ensureTokenBeforeSubmit()

    if (!tokenValid) {
      message.warning(
        'Your session has expired. Please log in again. Your data will be restored automatically.'
      )
      return
    }

    const incident = mapFormValuesToIncidentType(values)
    window.scrollTo({ top: 0, behavior: 'smooth' })
    dispatch(createIncident(incident))
  }

  const onHandleNewIncident = () => {
    form.resetFields()
    clearFormData()
    dispatch(setStatus(StatusE.IDLE))
  }

  return status === StatusE.CREATED ? (
    <Result
      status="success"
      title={DEFAULT_SUCCESS_MESSAGE}
      subTitle={response?.message}
      extra={[
        <Button key="create" type="primary" onClick={onHandleNewIncident}>
          Create another incident
        </Button>,
      ]}
    />
  ) : (
    <Spin spinning={status === StatusE.LOADING}>
      <Form
        form={form}
        layout="vertical"
        scrollToFirstError
        size="large"
        onFinish={onFinish}
        onFieldsChange={() => {
          setFormTouched(true)
        }}
      >
        <IncidentDetailsFormSection />
        <IncidentPeopleFormSection />
        <IncidentVehiclesFormSection />
        <Form.Item>
          <Typography.Paragraph
            type="secondary"
            style={styles.acknowledgeContent as React.CSSProperties}
          >
            A person that is marked as offender will be identified as a rogue guest to users of Good
            To Book, who will be advised against accepting future bookings from that person. Please
            ensure the data you provide is accurate.
          </Typography.Paragraph>
        </Form.Item>
        <Form.Item style={styles.submitButtonContainer}>
          <Button type="primary" htmlType="submit" loading={status === StatusE.LOADING}>
            Submit incident report
          </Button>
        </Form.Item>
      </Form>
    </Spin>
  )
}
